import { fromCents } from '../../moneyUtils'

// Shared encode/decode for AddBenefitWizard's "Per rule" rating tables —
// docs/2026-09-04-benefit-pricing-wizard-design.md §5/§9. Kept in one place
// so the wizard's generator and the edit-mode decompiler can never drift
// apart: a table is only ever wizard-editable if `parseRelationshipExpression`
// can invert exactly what `buildRelationshipExpression` produces.

export const buildRelationshipExpression = (relationships) => {
  if (relationships.length === 1) return `dependant.relationship == '${relationships[0]}'`
  const list = relationships.map((r) => `'${r}'`).join(',')
  return `dependant.relationship In [${list}]`
}

const SINGLE_RE = /^dependant\.relationship == '([^']+)'$/
const MULTI_RE = /^dependant\.relationship In \[('[^']+'(?:,'[^']+')*)\]$/

// Returns the relationships array the expression encodes, or null if it
// doesn't match the wizard's own generator pattern exactly (e.g. hand-written
// segment expressions, extra conditions, different fact refs).
export const parseRelationshipExpression = (expression) => {
  const single = SINGLE_RE.exec(expression || '')
  if (single) return [single[1]]
  const multi = MULTI_RE.exec(expression || '')
  if (multi) return multi[1].split(',').map((s) => s.slice(1, -1))
  return null
}

const NODE_TYPE_FOR_TARGET = {
  cover: 'multi_factor_rating',
  premium: 'multi_factor_rating',
  waiting_period: 'waiting_period_rating'
}

// Decompiles one target (cover/waiting_period/premium) of a benefit back
// into wizard state, or reports it can't be represented in the wizard.
// `valueField`: 'amount' (money targets) or 'cardinality' (waiting period) —
// determines which key the reconstructed rules carry.
//
// A failure still carries `probableMode` — a best-effort guess (from just the
// presence of a linked table, before any of the strict shape checks) at
// whether the real value varies by relationship at all, so the UI can at
// least pre-select "Per rule" instead of defaulting to the more misleading
// "All" when the exact rules can't be reconstructed. No tableGuid/tableVersion
// is returned on failure — the actual segments/cells are unknown, so nothing
// here should ever be used as a target for updateRatingTable.
export const decompileTarget = (nodeGuid, ratingNodes, ratingTables, target, valueField) => {
  if (!nodeGuid) return { decompilable: true, mode: 'all' }

  const node = (ratingNodes || []).find((n) => n.guid === nodeGuid)
  const probableMode = node && node.tables && node.tables.length > 0 ? 'per_rule' : 'all'
  if (!node) return { decompilable: false, probableMode: 'all' }
  if (node.node_type !== NODE_TYPE_FOR_TARGET[target]) return { decompilable: false, probableMode }
  if (!node.tables || node.tables.length !== 1) return { decompilable: false, probableMode }

  const table = (ratingTables || []).find((t) => t.guid === node.tables[0].rating_table_guid)
  if (!table) return { decompilable: false, probableMode }
  if (table.table_type !== 'base_rate') return { decompilable: false, probableMode }
  if (!table.dimensions || table.dimensions.length !== 1 || table.dimensions[0] !== 'dependant.relationship') {
    return { decompilable: false, probableMode }
  }

  const segments = table.segments || []
  const cells = table.cells || []
  if (segments.length === 0 || cells.length !== segments.length) return { decompilable: false, probableMode }

  const rules = []
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    const relationships = parseRelationshipExpression(segment.expression)
    if (!relationships) return { decompilable: false, probableMode }
    const cell = cells.find((c) => (c.segment_refs || []).length === 1 && c.segment_refs[0] === segment.label)
    if (!cell || cell.value_in_cents === undefined || cell.value_in_cents === null) return { decompilable: false, probableMode }
    const rule = { relationships, id: `rule-${i}` }
    rule[valueField] = valueField === 'amount' ? fromCents(cell.value_in_cents) : cell.value_in_cents
    rules.push(rule)
  }

  return { decompilable: true, mode: 'per_rule', rules, tableGuid: table.guid, nodeGuid: node.guid, tableVersion: table.version }
}
